import { ActionIcon, Button, Group, Loader, Modal, Stack, Text, Textarea } from "@mantine/core";
import { Archive, ArrowUp, Image as ImageIcon, Type } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";

import { useGetAsset } from "../../api/generated/endpoints/assets/assets";
import { useGetCatalog, useGetJob } from "../../api/generated/endpoints/generation/generation";
import type { Asset, CatalogImageModel, Job } from "../../api/generated/model";
import { Choice } from "../../ui/Choice";
import { ModelPicker } from "../../ui/ModelPicker";
import { Price } from "../../ui/Price";
import { RatioGlyph } from "../../ui/RatioGlyph";
import { Soon } from "../../ui/Soon";
import { ErrorAlert } from "../ErrorAlert";
import { failedWords } from "../jobs/failed-words";
import { usePricedStart } from "../jobs/priced-start";
import { canChange, jobRefetchInterval } from "../jobs/refetch";
import { useSaveOutput } from "../jobs/SaveOutput";
import { useStreamStatus } from "../stream-status";
import classes from "./GenerateImageDialog.module.css";
import { savingPollInterval, useSavingReads } from "./saving-poll";

/** Live's words (`image-generation-select-modal`, `constants/images.tsx`). */
const TITLE = "Generate image";
const PLACEHOLDER = "Describe your image...";
const SAVE_FAILED = "Failed to save image";
/** Live's `MIN_PROMPT_LENGTH`: the prompt, trimmed, before the arrow wakes. */
export const MIN_IMAGE_PROMPT = 10;

export interface GenerateImage {
  /** Opens the dialog, on the image made last if it was not saved (live keeps it). */
  open: () => void;
  /** The dialog while it is open. */
  dialog: ReactNode;
}

export interface GenerateImageOptions {
  teamId: string;
  brandId: string;
  /** Live's title for the slot: "Generate frame" from a video's frames, else "Generate image". */
  title?: string;
  /** The saved image, ready in the library: the slot takes it, and the dialog closes. */
  onSaved: (asset: Asset) => void;
}

/**
 * Live's "Generate" in an image slot (#598, `ImageGenerationSelectModal`, design-ref/sound L01j):
 * a dialog that makes one image as the Image studio's simple mode does, shows it as the result,
 * and saves it to the brand's library, where the slot takes it. The image made stays until it is
 * saved, across the dialog's openings, as live keeps it per slot.
 */
export function useGenerateImage({
  teamId,
  brandId,
  title = TITLE,
  onSaved,
}: GenerateImageOptions): GenerateImage {
  const [opened, setOpened] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  return {
    open: () => setOpened(true),
    dialog: opened ? (
      <GenerateImageDialog
        teamId={teamId}
        brandId={brandId}
        title={title}
        jobId={jobId}
        onStarted={(job) => setJobId(job.id)}
        onClose={() => setOpened(false)}
        onSaved={(asset) => {
          setJobId(null);
          setOpened(false);
          onSaved(asset);
        }}
      />
    ) : null,
  };
}

interface DialogProps {
  teamId: string;
  brandId: string;
  title: string;
  jobId: string | null;
  onStarted: (job: Job) => void;
  onClose: () => void;
  onSaved: (asset: Asset) => void;
}

function GenerateImageDialog(props: DialogProps) {
  const catalog = useGetCatalog().data;
  // The models the simple mode offers: an image from a prompt alone.
  const models = (catalog?.image_models ?? []).filter((each) =>
    each.job_types.includes("image.simple"),
  );
  return (
    <Modal
      opened
      onClose={props.onClose}
      title={props.title}
      size={820}
      centered
      classNames={{ content: classes.content, body: classes.body, title: classes.title }}
    >
      {catalog === undefined ? (
        <Loader size="sm" aria-label="Loading the models" />
      ) : models.length === 0 ? (
        <Text size="sm">No image model is offered here yet.</Text>
      ) : (
        <DialogBody {...props} models={models} />
      )}
    </Modal>
  );
}

function DialogBody(props: DialogProps & { models: CatalogImageModel[] }) {
  const { teamId, brandId, jobId, models, onStarted, onSaved } = props;
  const [model, setModel] = useState(() => models.find((each) => each.is_default) ?? models[0]!);
  const [ratio, setRatio] = useState(model.aspect_ratios.default);
  const [prompt, setPrompt] = useState("");
  const stream = useStreamStatus();
  const job = useGetJob(teamId, jobId ?? "", {
    query: {
      enabled: jobId !== null,
      refetchInterval: (query) => jobRefetchInterval(query.state.data?.status, stream),
    },
  });
  const status = jobId === null ? undefined : job.data?.status;
  const output = status === "succeeded" ? job.data?.outputs[0] : undefined;
  const image = output?.url ?? null;
  const running = jobId !== null && canChange(status);
  const failed = jobId !== null && !running && image === null;

  // The price of what the arrow would make (live prices the model and the ratio, not the prompt).
  const quote = { type: "image.simple" as const, model: model.id, aspect_ratio: ratio, outputs: 1 };
  const written = prompt.trim();
  const request =
    written.length >= MIN_IMAGE_PROMPT && !running ? { ...quote, prompt: written } : undefined;
  const priced = usePricedStart({ teamId, brandId, quote, request, onStarted });

  // The save answers `pending` while the server copies the file; the slot takes it once ready.
  const [copying, setCopying] = useState<string | null>(null);
  const save = useSaveOutput({
    teamId,
    brandId,
    jobId: jobId ?? "",
    index: output?.index ?? 0,
    onSaved: (asset) => (asset.status === "ready" ? onSaved(asset) : setCopying(asset.id)),
  });
  const copy = useGetAsset(teamId, brandId, copying ?? "", {
    query: {
      enabled: copying !== null,
      retry: false,
      refetchInterval: (query) =>
        savingPollInterval(query.state.data?.status, query.state.dataUpdateCount),
    },
  });
  const reads = useSavingReads(copy, teamId, brandId, copying ?? "");
  const copied = copy.data?.status === "ready" ? copy.data : undefined;
  const handedOver = useRef<string | null>(null);
  useEffect(() => {
    if (copied === undefined || handedOver.current === copied.id) return;
    handedOver.current = copied.id;
    onSaved(copied);
  }, [copied, onSaved]);
  const copyEnded =
    copying !== null &&
    (copy.isError ||
      (copy.data !== undefined &&
        savingPollInterval(copy.data.status, reads) === false &&
        copy.data.status !== "ready"));
  const saving = save.saving || (copying !== null && !copyEnded);

  const busy = running || priced.start.isPending || saving;
  const choose = (id: string) => {
    const next = models.find((each) => each.id === id);
    if (next === undefined) return;
    setModel(next);
    if (!next.aspect_ratios.values.includes(ratio)) setRatio(next.aspect_ratios.default);
  };
  const soon = (label: string, icon: ReactNode) => (
    <Soon>
      {(soonProps) => (
        <ActionIcon {...soonProps} variant="subtle" size={36} aria-label={label}>
          {icon}
        </ActionIcon>
      )}
    </Soon>
  );
  const shape = ratio.replace(":", " / ");
  const words = failedWords("image.simple", job.data?.error?.message);
  return (
    <>
      <Stack gap={16} className={classes.scrollable}>
        <div className={classes.panel}>
          <Textarea
            variant="unstyled"
            aria-label="Prompt"
            placeholder={PLACEHOLDER}
            autosize
            minRows={1}
            maxRows={6}
            maxLength={model.prompt_max_chars}
            disabled={busy}
            value={prompt}
            onChange={(event) => setPrompt(event.currentTarget.value)}
          />
          <hr className={classes.rule} />
          <Group justify="space-between" wrap="nowrap" gap={12}>
            {/* Live's three tools: Coming soon here (issue 598, SA2 decision 9). */}
            <Group gap={4} wrap="nowrap">
              {soon("Products", <Archive size={20} strokeWidth={1.5} />)}
              {soon("Reference images", <ImageIcon size={20} strokeWidth={1.5} />)}
              {soon("Prompt templates", <Type size={20} strokeWidth={1.5} />)}
            </Group>
            <Group gap={12} wrap="nowrap">
              {/* Near the dialog's top: the list opens downward, with the room below. */}
              <ModelPicker models={models} value={model.id} onChange={choose} below />
              <Choice
                setting="Aspect ratio"
                icon={<RatioGlyph ratio={ratio} />}
                width={100}
                value={ratio}
                options={model.aspect_ratios.values.map((each) => ({ value: each, label: each }))}
                onChange={setRatio}
              />
              <Price priced={priced.priced} pending={priced.pricing} />
              <ActionIcon
                variant="filled"
                size="lg"
                radius="100%"
                aria-label="Generate"
                disabled={!priced.canStart || busy}
                loading={priced.start.isPending}
                onClick={priced.startJob}
              >
                <ArrowUp size={20} />
              </ActionIcon>
            </Group>
          </Group>
        </div>
        {priced.quoteError === null ? null : <ErrorAlert error={priced.quoteError} />}
        {priced.start.isError ? <ErrorAlert error={priced.start.error} /> : null}
        <Text className={classes.result}>Result</Text>
        <div className={classes.card} style={{ aspectRatio: shape }}>
          {image !== null ? (
            <img src={image} alt="Result" className={classes.picture} />
          ) : running ? (
            <Loader size="sm" aria-label="Generating the image" />
          ) : failed ? (
            <Stack gap={4} className={classes.failed} role="alert">
              <Text size="sm" fw={600}>
                {words.title}
              </Text>
              <Text size="xs">{words.description}</Text>
            </Stack>
          ) : (
            <ImageIcon size={40} strokeWidth={1} aria-hidden className={classes.placeholder} />
          )}
        </div>
      </Stack>
      <Stack gap={8} align="center" className={classes.footer}>
        {save.saved ? null : save.result}
        {copyEnded ? (
          <Text size="sm" role="alert">
            {SAVE_FAILED}
          </Text>
        ) : null}
        <Button
          variant="filled-light"
          disabled={image === null || running || saving}
          loading={saving}
          onClick={save.save}
        >
          Save
        </Button>
      </Stack>
    </>
  );
}
